import { CheckIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { projectDecision, useProjects } from "../../projects/projectContext";
import { useWorkspace } from "../../shell/workspace";
import { appPaths } from "../../shared/routing/appPaths";
import AppButton from "../../ui/buttons/AppButton";
import { useHerePath, useThreadPreparation } from "../threads/prepare";
import { useChatDestinations } from "./referenceTray";

/**
 * Where a reference just sent to the chat went (F96, TR-B3-03): the Threads whose composer now
 * shows it, by title, and "Show" to bring the first of them into view beside, since its tab
 * may be open but hidden behind another one (the workspace owner focuses it or opens it
 * beside). When no open Thread takes messages, it waits, and "New Thread beside" starts one
 * through the Thread view's preparation, as the project's decision on proposing says (as the
 * Threads list offers New Thread, Sol F1); the reference goes with its first message. A saved
 * Thread's composer shows it too, beside its own Fork (TR-B3-01).
 */
export default function SentToChat({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const preparation = useThreadPreparation();
  // The permissions known are the selected project's: the reference's project may be another, open beside (Sol G1).
  const starting = projectDecision(useProjects(), projectId, "propose_change");
  const here = useHerePath();
  const takingMessages = useChatDestinations(projectId).filter((destination) => destination.takesMessages);
  const [first] = takingMessages;
  if (first !== undefined) {
    return (
      <>
        <span className="app-text-muted inline-flex items-center gap-1 px-2 text-xs" role="status">
          <CheckIcon className="h-4 w-4" aria-hidden="true" />
          {t("chatReferences.sentTo", { threads: takingMessages.map((destination) => `“${destination.title}”`).join(", ") })}
        </span>
        <AppButton variant="ghost" size="xs" onClick={() => { workspace.open(appPaths.thread(first.threadId), { placement: "beside" }); }}>
          {t("chatReferences.show")}
        </AppButton>
      </>
    );
  }
  return (
    <>
      <span className="app-text-muted px-2 text-xs" role="status">
        {t("chatReferences.waiting")}
      </span>
      <AppButton
        variant="ghost"
        size="xs"
        requires={starting}
        onClick={() => {
          preparation.prepare({ kind: "run", projectId, returnTo: here, workflow: null, entries: [], corpusVersion: null });
        }}
      >
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("chatReferences.newThread")}
      </AppButton>
    </>
  );
}
